import React, { useState } from 'react';
import {
  Button,
  IconButton,
  MenuItem,
  SegmentedButton,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { Divider, InputAdornment, Select, Stack, TextField, useTheme } from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Cancel from '@mui/icons-material/Cancel';
import Check from '@mui/icons-material/Check';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';

export type IncentiveCategory = { name: string, subcategories: string[] };

interface AddIncentivesStepProps {
  assignedProgram?: ApiRewardsProgram,
  categories: IncentiveCategory[],
  isEditing?: boolean,
  isNameDuplicate: boolean,
  hasPointsEngineBeenRun?: boolean,
  name: string,
  onChangeCategories: (categories: IncentiveCategory[]) => void,
  onChangeName: (name: string) => void,
}

export const AddIncentivesStep = ({
  assignedProgram,
  categories,
  isEditing = false,
  isNameDuplicate,
  hasPointsEngineBeenRun = false,
  name,
  onChangeCategories,
  onChangeName,
}: AddIncentivesStepProps) => {
  const { openSnackbar } = useSnackbar();
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState<'category' | 'subcategory' | null>(null);
  const [parentName, setParentName] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');

  const isSubcategoryNameValid = (
    !!subcategoryName
    && !categories
      .find(({ name }) => name === parentName)
      ?.subcategories.some((name) => name === subcategoryName)
  );
  const isParentNameValid = (
    !!parentName
    && !categories.some((category) => category.name === parentName)
  );

  const NameInput = (
    <Stack width="512px">
      <Text category="title-small">
        Mapping name
      </Text>
      <VSpacer size="5" />
      <Stack>
        <Stack alignItems="center" direction="row">
          <TextField
            disabled={!isEditing}
            error={isNameDuplicate}
            fullWidth
            label="Name"
            onChange={(e) => onChangeName(e.target.value)}
            value={name}
          />
        </Stack>
        {isNameDuplicate && (
          <Stack>
            <VSpacer size="3" />
            <Text category="body-medium" color="error">
              A category mapping with this name already exists.
            </Text>
          </Stack>
        )}
      </Stack>
      <VSpacer size="6" />
    </Stack>
  );

  const TypeSelector = (
    <SegmentedButton
      color="primary"
      fullWidth
      selectedIndex={selectedType && (selectedType === 'category' ? 0 : 1)}
    >
      <Button
        aria-selected={selectedType === 'category'}
        disabled={hasPointsEngineBeenRun || !isEditing}
        onClick={() => {
          if (selectedType !== 'category') {
            setSelectedType('category');
            setParentName('');
            setSubcategoryName('');
          }
        }}
        startIcon={selectedType === 'category' && <Check />}
        testID="category-button"
      >
        Category
      </Button>
      <Button
        aria-selected={selectedType === 'subcategory'}
        disabled={!categories.length || hasPointsEngineBeenRun || !isEditing}
        onClick={() => {
          if (selectedType !== 'subcategory') {
            setSelectedType('subcategory');
            setParentName('');
            setSubcategoryName('');
          }
        }}
        startIcon={selectedType === 'subcategory' && <Check />}
        testID="subcategory-button"
      >
        Subcategory
      </Button>
    </SegmentedButton>
  );

  const ParentCategoryInput = (
    <Stack width="512px">
      <Text category="title-small">
        Incentive category
      </Text>
      <VSpacer size="5" />
      <Stack alignItems="center" direction="row" gap="16px">
        <TextField
          InputProps={{
            endAdornment: !!parentName && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setParentName('')}
                  testID="parent-name-clear-button"
                >
                  <Cancel />
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          label="Name"
          onChange={(e) => setParentName(e.target.value)}
          value={parentName}
        />
        <IconButton
          disabled={!isParentNameValid}
          onClick={() => {
            setSelectedType(null);
            onChangeCategories([...categories, { name: parentName, subcategories: [] }]);
            openSnackbar('Category added');
          }}
          sx={{
            bgcolor: theme.palette.success.main,
            '&:hover': {
              bgcolor: theme.palette.success.main,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: theme.palette.grey[300],
            },
          }}
          testID="parent-category-save-button"
          variant="filled"
        >
          <Check sx={{ color: 'white', width: '24px' }} />
        </IconButton>
      </Stack>
    </Stack>
  );

  const SubcategoryInput = (
    <Stack width="512px">
      <Text category="title-small">
        Parent category
      </Text>
      <VSpacer size="5" />
      <Select
        onChange={(e) => setParentName(e.target.value as string)}
        value={parentName}
      >
        {categories.map(({ name }) => (
          <MenuItem key={name} testID={`parent-category-menu-item-${name}`} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
      <VSpacer size="7" />
      <Text category="title-small">
        Incentive subcategory
      </Text>
      <VSpacer size="5" />
      <Stack alignItems="center" direction="row" gap="16px">
        <TextField
          InputProps={{
            endAdornment: !!subcategoryName && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSubcategoryName('')}
                  testID="subcategory-name-clear-button"
                >
                  <Cancel />
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          label="Name"
          onChange={(e) => setSubcategoryName(e.target.value)}
          value={subcategoryName}
        />
        <IconButton
          disabled={!isSubcategoryNameValid}
          onClick={() => {
            setSelectedType(null);
            onChangeCategories(categories.map((category) => ({
              ...category,
              subcategories: category.name === parentName
                ? [...category.subcategories, subcategoryName]
                : category.subcategories,
            })));
            openSnackbar('Category added');
          }}
          sx={{
            bgcolor: theme.palette.success.main,
            '&:hover': {
              bgcolor: theme.palette.success.main,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: theme.palette.grey[300],
            },
          }}
          testID="parent-category-save-button"
          variant="filled"
        >
          <Check sx={{ color: 'white', width: '24px' }} />
        </IconButton>
      </Stack>
    </Stack>
  );

  const CategoryList = (
    <Stack divider={<Divider light />} width="512px">
      <Divider light />
      {categories
        .sort((a, b) => a.name.localeCompare(b.name))
        .flatMap((category) => ([
          <Stack
            alignItems="center"
            direction="row"
            gap="16px"
            justifyContent="space-between"
            key={category.name}
            py="8px"
          >
            <Text category="body-large" sx={{ py: '8px' }}>
              {category.name}
            </Text>
            {!hasPointsEngineBeenRun && isEditing && (
              <IconButton
                onClick={() => {
                  const newCategories = categories.filter((c) => c.name !== category.name);
                  if (!newCategories.length) {
                    setSelectedType(null);
                  }
                  onChangeCategories(newCategories);
                }}
                testID={`${category.name}-delete-button`}
              >
                <DeleteOutlined/>
              </IconButton>
            )}
          </Stack>,
          ...category.subcategories
            .sort((a, b) => a.localeCompare(b))
            .map((subcategoryName) => (
            <Stack
              alignItems="center"
              direction="row"
              gap="16px"
              justifyContent="space-between"
              key={`${category.name}-${subcategoryName}`}
              py="8px"
            >
              <Text category="body-large" sx={{ py: '8px' }}>
                {category.name}: {subcategoryName}
              </Text>
              {!hasPointsEngineBeenRun && isEditing && (
                <IconButton
                  onClick={() => {
                    const newCategories = categories.map((c) => ({
                      ...c,
                      subcategories: c.name === category.name
                        ? c.subcategories.filter((existingName) => (
                          existingName !== subcategoryName
                        ))
                        : c.subcategories,
                    }));
                    onChangeCategories(newCategories);
                  }}
                  testID={`${category.name}-delete-button`}
                >
                  <DeleteOutlined />
                </IconButton>
              )}
            </Stack>
            )),
        ]))}
      {!!categories.length && <Divider light />}
    </Stack>
  );

  return (
    <Stack alignItems="center">
      <Text category="headline-large">
        Step 1/2: Add Incentives
      </Text>
      {isEditing && !!assignedProgram && (
        <>
          <VSpacer size="3" />
          <Text category="body-medium" color="error">
            Changing incentive categories will impact the assigned program: {assignedProgram.name}.
          </Text>
        </>
      )}
      <VSpacer size="8" />
      {NameInput}
      <VSpacer size="8" />
      <Stack width="512px">
        <Text category="title-small">
          Incentive type
        </Text>
        <VSpacer size="3" />
        {TypeSelector}
      </Stack>
      {selectedType === 'category' && (
        <>
          <VSpacer size="7" />
          {ParentCategoryInput}
        </>
      )}
      {selectedType === 'subcategory' && (
        <>
          <VSpacer size="7" />
          {SubcategoryInput}
        </>
      )}
      <VSpacer size="8" />
      <Divider light sx={{ width: '100%' }} />
      <VSpacer size="8" />
      {!!categories.length && CategoryList}
    </Stack>
  );
};
